/**
 * Mirrors backend enums for school-classification dashboard (budget + review).
 * API may send numeric enum values or string names (camelCase / PascalCase).
 */

export type BudgetSubmittedStatusKind = 0 | 1 | 2 | 3;
export type ReviewStatusKind = 0 | 1 | 2 | 3;

const normKey = (s: string) => s.trim().toLowerCase().replace(/[\s_-]/g, '');

/** BudgetSubmitedStatus: Draft=0, Submited=1, ReturnForEditing=2, RequestForEditing=3 */
export function parseBudgetSubmittedStatus(raw: unknown): BudgetSubmittedStatusKind | null {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number' && Number.isInteger(raw) && raw >= 0 && raw <= 3) {
    return raw as BudgetSubmittedStatusKind;
  }
  const s = normKey(String(raw));
  const byName: Record<string, BudgetSubmittedStatusKind> = {
    draft: 0,
    submited: 1,
    submitted: 1,
    returnforediting: 2,
    requestforediting: 3,
  };
  if (byName[s] !== undefined) return byName[s];
  if (s.length === 1 && s >= '0' && s <= '3') return Number(s) as BudgetSubmittedStatusKind;
  return null;
}

export function labelBudgetSubmittedStatus(kind: BudgetSubmittedStatusKind): string {
  switch (kind) {
    case 0:
      return 'Draft';
    case 1:
      return 'Submitted';
    case 2:
      return 'Return for editing';
    case 3:
      return 'Request for editing';
    default:
      return '—';
  }
}

/** ReviewStatus: NoAction=0, Clarify=1, UnderReview=2, Corrected=3 */
export function parseReviewStatus(raw: unknown): ReviewStatusKind | null {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number' && Number.isInteger(raw) && raw >= 0 && raw <= 3) {
    return raw as ReviewStatusKind;
  }
  const s = normKey(String(raw));
  const byName: Record<string, ReviewStatusKind> = {
    noaction: 0,
    clarify: 1,
    underreview: 2,
    corrected: 3,
  };
  if (byName[s] !== undefined) return byName[s];
  if (s.length === 1 && s >= '0' && s <= '3') return Number(s) as ReviewStatusKind;
  return null;
}

export function labelReviewStatus(kind: ReviewStatusKind): string {
  switch (kind) {
    case 0:
      return 'No action';
    case 1:
      return 'Clarify';
    case 2:
      return 'Under review';
    case 3:
      return 'Corrected';
    default:
      return '—';
  }
}

function resolveBudgetKind(kind: BudgetSubmittedStatusKind | null | undefined, hint?: string): BudgetSubmittedStatusKind | null {
  if (kind !== undefined && kind !== null) return kind;
  return hint ? parseBudgetSubmittedStatus(hint) : null;
}

function resolveReviewKind(kind: ReviewStatusKind | null | undefined, hint?: string): ReviewStatusKind | null {
  if (kind !== undefined && kind !== null) return kind;
  return hint ? parseReviewStatus(hint) : null;
}

export function budgetSubmittedStatusBadgeClass(
  kind: BudgetSubmittedStatusKind | null | undefined,
  displayHint?: string
): string {
  const k = resolveBudgetKind(kind ?? null, displayHint);
  switch (k) {
    case 0:
      return 'bg-amber-100 text-amber-950 ring-1 ring-amber-200';
    case 1:
      return 'bg-emerald-100 text-emerald-900 ring-1 ring-emerald-200';
    case 2:
      return 'bg-rose-100 text-rose-900 ring-1 ring-rose-200';
    case 3:
      return 'bg-sky-100 text-sky-900 ring-1 ring-sky-200';
    default:
      return 'bg-slate-100 text-slate-800 ring-1 ring-slate-200';
  }
}

export function reviewStatusBadgeClass(kind: ReviewStatusKind | null | undefined, displayHint?: string): string {
  const k = resolveReviewKind(kind ?? null, displayHint);
  switch (k) {
    case 0:
      return 'bg-slate-100 text-slate-800 ring-1 ring-slate-200';
    case 1:
      return 'bg-amber-100 text-amber-950 ring-1 ring-amber-200';
    case 2:
      return 'bg-indigo-100 text-indigo-900 ring-1 ring-indigo-200';
    case 3:
      return 'bg-emerald-100 text-emerald-900 ring-1 ring-emerald-200';
    default:
      return 'bg-slate-100 text-slate-800 ring-1 ring-slate-200';
  }
}
